/**
 * A Manual Control funding bag (`type-id` -> amount) in the client's words: which account, how much.
 * Used wherever a deposit's funding is listed — the Suggestion Engine card's allocation grid and the
 * plan recap — so the second room's deposit is shown from the same assets the first room's is
 * (owner, 7 Oct 2026: "where is the other $310K coming from").
 */
import { parseRadFundingAssetKey, findAssetInFactFind } from '../../utils/entityFundingUtils.ts';

const ASSET_TYPE_FALLBACKS: Record<string, string> = {
    bankAccounts: 'bank account',
    termDeposits: 'term deposit',
    accountBasedPensions: 'account-based pension',
    shares: 'shares',
    managedFunds: 'managed funds',
    investmentBonds: 'investment bond',
};

export interface FundingLine { key: string; label: string; amount: number }

export function describeFundingKeys(factFind: unknown, bag: Record<string, number | string> | null | undefined): FundingLine[] {
    return Object.entries(bag || {})
        .map(([key, raw]) => ({ key, amount: Number(raw) || 0 }))
        .filter((e) => e.amount > 0)
        .map(({ key, amount }) => {
            const parsed = parseRadFundingAssetKey(key);
            const located = parsed ? findAssetInFactFind(factFind as never, parsed.assetType, parsed.assetId) : null;
            const label = (located?.asset as { description?: string } | undefined)?.description || (parsed ? ASSET_TYPE_FALLBACKS[parsed.assetType] : null) || 'savings';
            return { key, label, amount };
        });
}

/** "$50,000 from anz, $60,000 from MLC and $150,000 from HUB" */
export function fundingPhrase(lines: FundingLine[], formatCurrency: (n: number) => string): string {
    const parts = lines.map((l) => `${formatCurrency(l.amount)} from ${l.label}`);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
